import type { ComparisonResult } from '@/reactQuery/comparison';
import { formatSectionsForClipboard, formatSummaryForClipboard } from './formatForClipboard';

/**
 * Escape pipe characters and newlines in markdown-table cells so a metric
 * name or filter name containing `|` (rare but possible in AI-generated
 * text) doesn't break the table structure when the file is rendered.
 */
function escapeCell(value: string | number): string {
  return String(value ?? '')
    .replace(/\|/g, '\\|')
    .replace(/\n/g, ' ');
}

/**
 * Formats a comparison result as structured Markdown for download.
 */
export function formatComparisonAsMarkdown(data: ComparisonResult): string {
  const lines: string[] = [];

  lines.push(`# Comparison: "${data.filter_a_name}" vs "${data.filter_b_name}"`);
  lines.push('');

  // Executive Summary
  if (data.narrative) {
    lines.push('## Executive Summary');
    lines.push('');
    lines.push(data.narrative);
    lines.push('');
  }

  // Statistical Comparison
  if (data.statistical_comparison.length > 0) {
    lines.push('## Statistical Overview');
    lines.push('');
    lines.push(
      `| Metric | ${escapeCell(data.filter_a_name)} | ${escapeCell(data.filter_b_name)} | Delta |`,
    );
    lines.push('|--------|--------|--------|-------|');
    for (const row of data.statistical_comparison) {
      lines.push(
        `| ${escapeCell(row.metric)} | ${escapeCell(row.filter_a_value)} | ${escapeCell(
          row.filter_b_value,
        )} | ${escapeCell(row.delta)} |`,
      );
    }
    lines.push('');
  }

  // Shared Themes
  if (data.shared_themes.length > 0) {
    lines.push('## Shared Themes');
    lines.push('');
    for (const theme of data.shared_themes) {
      lines.push(`### ${theme.title}`);
      lines.push('');
      lines.push(theme.body);
      lines.push('');
    }
  }

  // Unique to A
  if (data.unique_to_a.length > 0) {
    lines.push(`## Unique to ${data.filter_a_name}`);
    lines.push('');
    for (const item of data.unique_to_a) {
      lines.push(`### ${item.title}`);
      lines.push('');
      lines.push(item.body);
      lines.push('');
    }
  }

  // Unique to B
  if (data.unique_to_b.length > 0) {
    lines.push(`## Unique to ${data.filter_b_name}`);
    lines.push('');
    for (const item of data.unique_to_b) {
      lines.push(`### ${item.title}`);
      lines.push('');
      lines.push(item.body);
      lines.push('');
    }
  }

  // Per-Question Breakdown
  if (data.per_question && data.per_question.length > 0) {
    lines.push('## Per-Question Breakdown');
    lines.push('');
    for (const q of data.per_question) {
      lines.push(`### ${q.question_text}`);
      lines.push('');
      if (q.summary_a) {
        lines.push(`**${data.filter_a_name}:** ${q.summary_a}`);
        lines.push('');
      }
      if (q.summary_b) {
        lines.push(`**${data.filter_b_name}:** ${q.summary_b}`);
        lines.push('');
      }
      if (q.key_differences.length > 0) {
        lines.push('**Key Differences:**');
        for (const d of q.key_differences) {
          lines.push(`- **${d.title}**: ${d.body}`);
        }
        lines.push('');
      }
      if (q.shared_patterns.length > 0) {
        lines.push('**Shared Patterns:**');
        for (const p of q.shared_patterns) {
          lines.push(`- **${p.title}**: ${p.body}`);
        }
        lines.push('');
      }
    }
  }

  return lines.join('\n');
}

/**
 * Builds a plain-text version of the full report for clipboard copy.
 */
export function buildFullReportText(data: ComparisonResult): string {
  const perQuestionText =
    data.per_question && data.per_question.length > 0
      ? `Per-Question Comparison\n${data.per_question
          .map((q) => {
            const parts = [`Q: ${q.question_text}`];
            if (q.summary_a) parts.push(`${data.filter_a_name}: ${q.summary_a}`);
            if (q.summary_b) parts.push(`${data.filter_b_name}: ${q.summary_b}`);
            if (q.key_differences.length > 0) {
              parts.push(`Key Differences\n${formatSectionsForClipboard(q.key_differences)}`);
            }
            if (q.shared_patterns.length > 0) {
              parts.push(`Shared Patterns\n${formatSectionsForClipboard(q.shared_patterns)}`);
            }
            return parts.join('\n');
          })
          .join('\n\n---\n\n')}`
      : '';

  return [
    data.narrative ? `Executive Summary\n${formatSummaryForClipboard(data.narrative)}` : '',
    data.shared_themes.length > 0
      ? `Shared Themes\n${formatSectionsForClipboard(data.shared_themes)}`
      : '',
    data.unique_to_a.length > 0
      ? `Unique to ${data.filter_a_name}\n${formatSectionsForClipboard(data.unique_to_a)}`
      : '',
    data.unique_to_b.length > 0
      ? `Unique to ${data.filter_b_name}\n${formatSectionsForClipboard(data.unique_to_b)}`
      : '',
    perQuestionText,
  ]
    .filter(Boolean)
    .join('\n\n');
}
